import { memo } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { StateCard } from '@/lib/components/StateCard/StateCard';

import { useStateUserContext } from '@/src/application/context/UserContext';
import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';
import ScrollToTop from '@/src/application/util/ScrollToTop';
import { V1CompatHandler } from '@/src/compat/V1CompatHandlerContainer';
import AppLayout from '@/src/layouts/AppLayout';
import { IFreeTrialDemo } from '@/src/presentation/types/interfaces/IFreeTrialDemo';

import { CampaignsModuleRouter } from '@/modules/CampaignsModule/router/CampaignsModuleRouter';
import CreateSegmentModalProvider from '@/modules/ContactsModule/contexts/CreateSegmentModalContext';
import { ContactsModuleRouter } from '@/modules/ContactsModule/router/ContactsModuleRouter';
import { NpsModuleRouter } from '@/modules/NPSModule/router/NpsModuleRouter';
import { Home } from '@/modules/OnboardingModule/screens';

interface AppRouterVersionTwoProps {
  freeTrialBlockState: IFreeTrialDemo;
  contactsEnabled: boolean;
}

const AppRouterVersionTwo = ({
  freeTrialBlockState,
  contactsEnabled,
}: AppRouterVersionTwoProps) => {
  const showNpsModule = useFeatureFlag('showNpsModule');
  const showCampaigns = useFeatureFlag('showCampaigns');

  const { userData } = useStateUserContext();

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route element={<AppLayout userData={userData} />} path="/v2">
          <Route
            index
            element={
              <>
                <V1CompatHandler enabled={false} targetIframe={'/?v2=true'} />
                <Home freeTrialBlockState={freeTrialBlockState} userData={userData} />
              </>
            }
          />
          <Route
            element={<V1CompatHandler enabled={true} targetIframe={'/?v2=true'} />}
            path="dashboard"
          />
          <Route
            element={
              <CreateSegmentModalProvider>
                {contactsEnabled ? (
                  <>
                    <ContactsModuleRouter />
                    <V1CompatHandler enabled={false} targetIframe={'/?v2=true'} />
                  </>
                ) : (
                  <V1CompatHandler enabled targetIframe={'/?v2=true'} />
                )}
              </CreateSegmentModalProvider>
            }
            path="contacts/*"
          />
          <Route
            element={
              showCampaigns ? (
                <>
                  <CampaignsModuleRouter />
                  <V1CompatHandler enabled={false} tab="/v2/campaigns" targetIframe={'/?v2=true'} />
                </>
              ) : (
                <V1CompatHandler enabled targetIframe={'/?v2=true'} />
              )
            }
            path="campaigns/*"
          />
          <Route element={<V1CompatHandler enabled targetIframe={'/?v2=true'} />} path="reports" />
          <Route
            element={<V1CompatHandler enabled targetIframe={'/?v2=true'} />}
            path="automation"
          />
          <Route element={<V1CompatHandler enabled targetIframe={'/?v2=true'} />} path="onsite" />
          <Route
            element={<V1CompatHandler enabled targetIframe={'/?v2=true'} />}
            path="configuration"
          />
          <Route
            element={
              <>
                <V1CompatHandler enabled targetIframe={`/${window.location.search}&v2=true`} />
              </>
            }
            path="sharing"
          />
          {showNpsModule && (
            <Route
              element={
                <>
                  <NpsModuleRouter />
                  <V1CompatHandler enabled={false} targetIframe={'/?v2=true'} />
                </>
              }
              path="nps/*"
            />
          )}
        </Route>
      </Routes>
      <StateCard />
    </BrowserRouter>
  );
};

export default memo(AppRouterVersionTwo);
